import { useApolloClient } from "@apollo/client";
import { formatInvitationPairingCode } from "@msys/common";
import { Box, Button, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useLanguages } from "../../../common/translations/useLanguages.js";
import { ROUTER_BASENAME } from "../../../environment.js";
import { buildRegisterUrlWithPrefill } from "../../auth/buildRegisterUrlWithPrefill.js";
import { INVITATION_SCOPE } from "../../auth/signup/signupScope.js";
import { MeisterMark } from "../../auth/signup/MeisterMark.js";
import { useAuth } from "../../auth/useAuth.js";
import {
  ONBOARDING_BODY_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "../../features/organisations/onboarding/onboardingDesign.js";
import { REF_TOKEN_URL_SEARCH_PARAM } from "../../constants.js";
import { getCrmLinkingInvitationPrefill } from "../crmLinkingInvitationPrefill.js";
import { InvitationHeading } from "../InvitationHeading.js";
import { InvitationPage } from "../InvitationPage.js";
import {
  RefTokenDocument,
  type RefTokenQuery,
  type RefTokenQueryVariables,
} from "../InvitationTokenProvider.generated.js";
import { resolveInvitationTokenData } from "../resolveInvitationTokenData.js";
import { buildAppUrl } from "./appUrl.js";
import { deviceGrantTokenStorage } from "../../auth/deviceGrantTokenStorage.js";
import { CodeOrScanCard } from "./CodeOrScanCard.js";
import { DeviceSignInView } from "./DeviceSignInView.js";
import { navigateToExternal } from "./navigateToExternal.js";
import { useDeviceSignIn } from "./useDeviceSignIn.js";
import { useInvitationPairing } from "./useInvitationPairing.js";

const REGISTRATION_RETRY_DELAY_MS = 3000;

export const ContinueInvitationPage = () => {
  const { t } = useTranslate(["Global"]);
  const { auth, isAuthenticated } = useAuth();
  const { currentLanguage } = useLanguages();
  const client = useApolloClient();
  const pairing = useInvitationPairing({ enabled: !isAuthenticated });
  const [notContinuable, setNotContinuable] = React.useState(false);
  const [view, setView] = React.useState<"pairing" | "device">("pairing");
  const [storageFailed, setStorageFailed] = React.useState(false);
  const deviceSignIn = useDeviceSignIn({
    authServerUrl: auth.authServerUrl ?? "",
    realm: auth.realm ?? "",
    clientId: auth.clientId ?? "",
    onSuccess: tokens => {
      // Tab-scoped; AuthWebProvider picks the tokens up on the reload.
      // The device code is consumed: without stored tokens the reload would
      // land signed out, so show the error state instead of navigating.
      if (!deviceGrantTokenStorage.write(tokens)) {
        setStorageFailed(true);
        return;
      }
      navigateToExternal(buildAppUrl(location.origin, ROUTER_BASENAME, "/"));
    },
  });
  const cancelDeviceSignIn = deviceSignIn.cancel;
  React.useEffect(() => {
    // The claimed pairing wins: stop the device flow so a phone approval
    // cannot navigate the tab away mid-registration.
    if (pairing.status === "claimed" && view === "device") {
      cancelDeviceSignIn();
      setStorageFailed(false);
      setView("pairing");
    }
  }, [pairing.status, view, cancelDeviceSignIn]);
  const canSignInWithPhone = Boolean(
    auth.authServerUrl && auth.realm && auth.clientId
  );

  React.useEffect(() => {
    const refToken = pairing.refToken;
    if (pairing.status !== "claimed" || !refToken || notContinuable) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    // The claim is single-use server-side, so a failure here must be retried
    // rather than abandoned while the page is mounted and the refToken is held.
    const proceed = async () => {
      try {
        const { data } = await client.query<
          RefTokenQuery,
          RefTokenQueryVariables
        >({
          query: RefTokenDocument,
          variables: { refToken },
          fetchPolicy: "no-cache",
        });
        const invitation = resolveInvitationTokenData(data);
        // A successful lookup that is not an open CRM linking invitation is
        // permanent (e.g. accepted on the phone meanwhile): do not retry.
        if (invitation?.__typename !== "CrmLinkingInvitation") {
          if (!cancelled) setNotContinuable(true);
          return;
        }
        const prefill = getCrmLinkingInvitationPrefill(invitation.contact);
        const registerUrl = await auth.createRegisterUrl({
          locale: currentLanguage.locale,
          scope: INVITATION_SCOPE,
          redirectUri: `${buildAppUrl(location.origin, ROUTER_BASENAME, "/next")}?${REF_TOKEN_URL_SEARCH_PARAM}=${encodeURIComponent(refToken)}`,
        });
        if (cancelled) return;
        navigateToExternal(buildRegisterUrlWithPrefill(registerUrl, prefill));
      } catch (error) {
        Sentry.captureException(error);
        if (cancelled) return;
        retryTimer = setTimeout(() => {
          void proceed();
        }, REGISTRATION_RETRY_DELAY_MS);
      }
    };
    void proceed();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    pairing.status,
    pairing.refToken,
    notContinuable,
    client,
    auth,
    currentLanguage.locale,
  ]);

  const formattedCode = pairing.code
    ? formatInvitationPairingCode(pairing.code)
    : null;

  const body = (text: string) => (
    <Typography
      sx={{
        ...ONBOARDING_BODY_SX,
        mt: "11px",
        width: "100%",
        textWrap: "balance",
      }}
    >
      {text}
    </Typography>
  );

  return (
    // Shown on a fair-stand PC and read from a few metres, so the code and the
    // QR code get the room: one card, side by side on a desktop, stacked below.
    <InvitationPage isAuthenticated={isAuthenticated} contentMaxWidth={800}>
      <Stack sx={{ alignItems: "center" }}>
        <MeisterMark />
        <InvitationHeading sx={{ mt: "25px" }}>
          {view === "device" && !isAuthenticated && pairing.status !== "claimed"
            ? t("Sign in with your phone", { ns: "Global" })
            : t("Continue invitation", { ns: "Global" })}
        </InvitationHeading>
        {isAuthenticated ? (
          <Stack sx={{ width: "100%", maxWidth: 500 }}>
            {body(
              t(
                "You are signed in. Sign out to show a code for the next invitation.",
                { ns: "Global" }
              )
            )}
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                void auth.logout({
                  redirectUri: buildAppUrl(
                    location.origin,
                    ROUTER_BASENAME,
                    "/continue-invitation"
                  ),
                });
              }}
              sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
            >
              {t("Sign out and show a new code", { ns: "Global" })}
            </Button>
          </Stack>
        ) : pairing.status === "claimed" && notContinuable ? (
          <Stack sx={{ width: "100%", maxWidth: 500 }}>
            {body(
              t(
                "This invitation can no longer be continued on another device.",
                { ns: "Global" }
              )
            )}
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                setNotContinuable(false);
                pairing.restart();
              }}
              sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
            >
              {t("Retry", { ns: "Global" })}
            </Button>
          </Stack>
        ) : pairing.status === "claimed" ? (
          body(
            t("Invitation received. Starting registration…", { ns: "Global" })
          )
        ) : view === "device" ? (
          <DeviceSignInView
            state={storageFailed ? { status: "error" } : deviceSignIn.state}
            onRestart={() => {
              setStorageFailed(false);
              deviceSignIn.start();
            }}
            onBack={() => {
              setStorageFailed(false);
              deviceSignIn.cancel();
              setView("pairing");
            }}
          />
        ) : (
          <>
            {body(
              t(
                "Open the invitation link from your email on your phone and choose “Continue on another device”.",
                { ns: "Global" }
              )
            )}
            {formattedCode && pairing.code ? (
              <Box sx={{ width: "100%", mt: "32px" }}>
                <CodeOrScanCard
                  code={formattedCode}
                  qrValue={`${buildAppUrl(
                    location.origin,
                    ROUTER_BASENAME,
                    "/continue-invitation/join"
                  )}?code=${pairing.code}`}
                  data-testid="continue-invitation-code"
                />
              </Box>
            ) : pairing.status === "error" ? (
              <Typography
                color="error"
                sx={{ mt: "11px", textAlign: "center" }}
              >
                {t("The code could not be created. Trying again…", {
                  ns: "Global",
                })}
              </Typography>
            ) : null}
            {canSignInWithPhone ? (
              // A line of text and a small text button, like the signup
              // page's "Already have an account? Log in".
              <Typography
                component="div"
                sx={{
                  ...ONBOARDING_BODY_SX,
                  mt: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  columnGap: "4px",
                  fontSize: 14,
                }}
              >
                {t("Already registered on your phone?", { ns: "Global" })}
                <Button
                  variant="text"
                  data-testid="device-sign-in-start"
                  onClick={() => {
                    setView("device");
                    setStorageFailed(false);
                    deviceSignIn.start();
                  }}
                  sx={ONBOARDING_SMALL_TEXT_BUTTON}
                >
                  {t("Sign in with your phone", { ns: "Global" })}
                </Button>
              </Typography>
            ) : null}
          </>
        )}
      </Stack>
    </InvitationPage>
  );
};
